/**
 * State of a live game — one where everybody plays from their own phone and the
 * server deals, validates and scores.
 *
 * The same shape travels to the client, with private fields blanked per viewer:
 * `hand` is populated for you and `null` for everyone else, who expose only
 * `handCount`. That is why hands are nullable in the type rather than only in
 * the wire format — the client must be forced to handle "I cannot see this".
 */

import type { Card, GameConfig, ScoreRound, Trick, TrumpSuit } from './game';

export type LivePhase = 'lobby' | 'bidding' | 'playing' | 'scoring' | 'finished';

export interface LivePlayer {
  seat: number;
  name: string;
  profileId: string | null;
  /*
   * A copy of the face at the moment of joining, not a lookup key.
   *
   * Denormalised on purpose: the table would otherwise need every client to
   * fetch the profile list to draw a seat, and a live game is a snapshot
   * anyway. If someone changes their photo mid-game, the table keeps the one
   * everybody has been looking at all evening — which is the better answer.
   */
  avatar: string | null;
  emoji: string | null;
  isHost: boolean;
  isReady: boolean;
  isConnected: boolean;
  /** A seat the server plays itself, so the table can show it as such. */
  isBot: boolean;
  /** Your own cards; `null` means redacted because they are not yours. */
  hand: Card[] | null;
  handCount: number;
  /** Bid for the current round, or null before bidding reaches this seat. */
  bid: number | null;
  tricksWon: number;
  /** Running total across scored rounds. */
  total: number;
}

export interface LiveGameState {
  id: string;
  code: string;
  phase: LivePhase;
  config: GameConfig;
  players: LivePlayer[];

  /** 1-based; 0 while still in the lobby. */
  roundNo: number;
  /** Cards dealt this round. */
  cards: number;
  dealerSeat: number;
  trumpCard: Card | null;
  trumpSuit: TrumpSuit;

  /** Seat that led the current trick. */
  leaderSeat: number;
  currentTrick: Trick;
  /** Seat whose turn it is to bid or play; null when nobody is on the clock. */
  turnSeat: number | null;

  /** Cards left undealt. The cards themselves never leave the server. */
  deckCount: number;

  /** Completed rounds, in the same shape the scorekeeper uses. */
  rounds: ScoreRound[];

  /** Kept on screen briefly so players can see who took the last trick. */
  lastTrick: { winnerSeat: number; trick: Trick; trumpSuit: TrumpSuit } | null;

  createdAt: string;
  updatedAt: string;
}

export const MAX_LIVE_PLAYERS = 7;
export const MIN_LIVE_PLAYERS = 3;
